'use client';

import { useCallback, useSyncExternalStore } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { Cookie } from 'lucide-react';
import { buildLocalePath, type Locale } from '@/i18n/config';
import { Button } from '@/components/ui/button';
import {
  getCookieConsent,
  setCookieConsent,
  clearCookieConsent,
  subscribe,
  type CookieConsentValue,
} from '@/lib/cookie-consent';

function useCookieConsent(): CookieConsentValue | null | 'ssr' {
  return useSyncExternalStore(subscribe, getCookieConsent, () => 'ssr' as const);
}

/**
 * The banner is `fixed bottom-0` and would obstruct the primary CTA on flows
 * with a bottom-anchored action button (the registration wizard's Pay/Continue
 * bar, the donate flow). Suppress it on those routes; consent is still captured
 * on every other page. Matches `/register` and `/donate` with any locale prefix.
 */
const SUPPRESSED_ROUTE = /\/(register|donate)(\/|$)/;

export function CookieConsentBanner() {
  const consent = useCookieConsent();
  const pathname = usePathname();
  const locale = useLocale() as Locale;
  const t = useTranslations('consent');

  const handleAccept = useCallback(() => {
    setCookieConsent('accepted');
  }, []);

  const handleDecline = useCallback(() => {
    setCookieConsent('declined');
  }, []);

  // Hidden during SSR and when consent already given
  if (consent !== null) return null;
  // Hidden on flows where the fixed banner would cover the primary CTA
  if (pathname && SUPPRESSED_ROUTE.test(pathname)) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 bg-stone-950 text-white border-t border-white/10 p-4 sm:p-5">
      <div className="container flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3 text-sm text-white/80">
          <Cookie className="w-5 h-5 mt-0.5 shrink-0 text-white/50" />
          <p>
            {t('banner')}{' '}
            <Link
              href={buildLocalePath(locale, '/privacy')}
              className="underline underline-offset-2 hover:text-white"
              prefetch={false}
            >
              {t('learnMore')}
            </Link>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDecline}
            className="border-white/20 bg-stone-800 text-white hover:bg-white/10 hover:text-white"
          >
            {t('decline')}
          </Button>
          <Button
            size="sm"
            onClick={handleAccept}
            className="bg-white text-primary hover:bg-white/90"
          >
            {t('accept')}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Re-shows the cookie consent banner by clearing the stored consent.
 * Call this from a "Cookie Settings" link in the footer.
 */
export function useCookieConsentReset() {
  return useCallback(() => {
    clearCookieConsent();
  }, []);
}
